import type { ValueOf } from '#layers/base/public';
import * as z from 'zod/v4-mini';
import { BuiltinBlockIds } from '#layers/data-model/constants/builtin-block-ids';

/**
 * Supported SSL/TLS connection modes, shared across relational connectors.
 * Each connector opts into a subset of these via {@link sslSchema}.
 */
export const SslMode = {
	disable: 'disable',
	allow: 'allow',
	prefer: 'prefer',
	require: 'require',
	verifyCa: 'verify-ca',
	verifyFull: 'verify-full',
} as const;

export type SslMode = ValueOf<typeof SslMode>;

const ALLOWED_SSL_CERT_EXTENSIONS = [
	'p8',
	'key',
	'p10',
	'csr',
	'cer',
	'crl',
	'p7c',
	'crt',
	'pem',
	'p12',
	'pfx',
	'p7b',
	'spc',
	'p7r',
];

const CERT_UPLOAD_PROPS = {
	rows: 5,
	class: 'font-medium font-mono',
	encodeBase64: true,
	acceptExtensions: ALLOWED_SSL_CERT_EXTENSIONS,
};

interface SslSchemaOptions {
	/** Modes the connector exposes, in display order. */
	modes: SslMode[];
	/** Whether the connector supports mutual TLS (client cert/key/password). */
	clientAuth: boolean;
}

/**
 * Engine contract: a CA certificate is mandatory for `verify-ca`. `verify-full`
 * falls back to the system trust store, so it is allowed without one. The issue
 * is reported at the nested `ca_cert` path so the form surfaces it inline on the
 * CA field as `ssl.ca_cert`.
 */
const verifyCaCheck = z.refine<{ mode: SslMode; ca_cert?: string }>(
	(value) => value.mode !== SslMode.verifyCa || !!value.ca_cert,
	{ error: '$t:connectors.common.ca_cert_required', path: ['ca_cert'] },
);

/**
 * Build the zod schema for a connector's `ssl` field. When `clientAuth` is set,
 * the optional client certificate/key/password fields are included.
 */
export function sslSchema({ modes, clientAuth }: SslSchemaOptions) {
	const base = {
		mode: z.enum(modes),
		ca_cert: z.optional(z.string()),
	};

	if (!clientAuth) {
		return z.object(base).check(verifyCaCheck);
	}

	return z.object({
		...base,
		client_cert: z.optional(z.string()),
		client_key: z.optional(z.string()),
		client_key_password: z.optional(z.string()),
	}).check(verifyCaCheck);
}

const SSL_MODE_OPTION_LABELS = {
	[SslMode.disable]: {
		label: '$t:connectors.common.ssl_mode_disable',
		description: '$t:connectors.common.ssl_mode_disable_description',
	},
	[SslMode.allow]: {
		label: '$t:connectors.common.ssl_mode_allow',
		description: '$t:connectors.common.ssl_mode_allow_description',
	},
	[SslMode.prefer]: {
		label: '$t:connectors.common.ssl_mode_prefer',
		description: '$t:connectors.common.ssl_mode_prefer_description',
	},
	[SslMode.require]: {
		label: '$t:connectors.common.ssl_mode_require',
		description: '$t:connectors.common.ssl_mode_require_description',
	},
	[SslMode.verifyCa]: {
		label: '$t:connectors.common.ssl_mode_verify_ca',
		description: '$t:connectors.common.ssl_mode_verify_ca_description',
	},
	[SslMode.verifyFull]: {
		label: '$t:connectors.common.ssl_mode_verify_full',
		description: '$t:connectors.common.ssl_mode_verify_full_description',
	},
} as const;

export function sslModeOptions(modes: SslMode[]) {
	return modes.map((mode) => ({
		label: SSL_MODE_OPTION_LABELS[mode].label,
		description: SSL_MODE_OPTION_LABELS[mode].description,
		value: mode,
	}));
}

/** Resolved form values needed to compute SSL field visibility. */
interface SslFieldValues {
	ssl?: {
		mode?: SslMode;
		client_key?: string;
	};
}

export function caCertField(values: SslFieldValues) {
	return {
		name: 'ssl.ca_cert',
		label: '$t:connectors.common.ca_cert',
		description: '$t:connectors.common.ca_cert_description',
		block: BuiltinBlockIds.InputTextareaFile,
		hidden: values.ssl?.mode === SslMode.disable,
		// UX-only asterisk; the `verify-ca` requirement is enforced by `sslSchema`.
		required: values.ssl?.mode === SslMode.verifyCa,
		fieldProps: {
			layout: 'stacked',
		},
		props: CERT_UPLOAD_PROPS,
	};
}

export function clientAuthFields(values: SslFieldValues) {
	return [
		{
			name: 'ssl.client_cert',
			label: '$t:connectors.common.client_cert',
			description: '$t:connectors.common.client_cert_description',
			block: BuiltinBlockIds.InputTextareaFile,
			hidden: values.ssl?.mode === SslMode.disable,
			fieldProps: {
				layout: 'stacked',
			},
			props: CERT_UPLOAD_PROPS,
		},
		{
			name: 'ssl.client_key',
			label: '$t:connectors.common.client_key',
			description: '$t:connectors.common.client_key_description',
			block: BuiltinBlockIds.InputTextareaFile,
			hidden: values.ssl?.mode === SslMode.disable,
			fieldProps: {
				layout: 'stacked',
			},
			props: CERT_UPLOAD_PROPS,
		},
		{
			name: 'ssl.client_key_password',
			label: '$t:connectors.common.client_key_password',
			description: '$t:connectors.common.client_key_password_description',
			block: BuiltinBlockIds.InputPassword,
			hidden: values.ssl?.mode === SslMode.disable || !values.ssl?.client_key,
			props: {
				autocomplete: 'off',
			},
		},
	];
}
